import React, { useRef, useState } from 'react';
import '../styles/ContactForm.scss';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { Snackbar, Alert } from '@mui/material';
const Arrow = `${process.env.REACT_APP_S3_URL}rightarrow.png`
const RightArrow = `${process.env.REACT_APP_S3_URL}RightArrowRedBg.svg`;
const Logo = `${process.env.REACT_APP_S3_URL}mobilewarelogo.svg`;

const intitalData = {
    name: '',
    company: '',
    phone: '',
    email: '',
    message: ''
}

const ContactForm: React.FC = () => {
    const [formData, setFormData] = useState(intitalData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'success' | 'error'>('success');
    const recaptchaRef = useRef<ReCAPTCHA | null>(null);
    const recaptchaTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData({ ...formData, [e.target.id]: e.target.value });
    };

      const onChange = (token: string | null) => {
        // reCAPTCHA callback
    };

    const showToast = (message: string, type: 'success' | 'error') => {
        setToastMessage(message);
        setToastType(type);
        setToastOpen(true);
    };

    const handleToastClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setToastOpen(false);
    };

    const validateForm = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\d{10,}$/; // Allow 10 or more digits for international

        if (!formData.name.trim()) {
            showToast("Name is required.", 'error');
            return false;
        }
        else if (!formData.company.trim()) {
            showToast("Company is required.", 'error');
            return false;
        }
        else if (!phoneRegex.test(formData.phone)) {
            showToast("Phone number must be 10 digits.", 'error');
            return false;
        }
        else if (!emailRegex.test(formData.email)) {
            showToast("Invalid email format.", 'error');
            return false;
        }
        else if (!formData.message.trim()) {
            showToast("Message is required.", 'error');
            return false;
        }
        return true;
    }
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        try {
            setLoading(true);

            if (!recaptchaRef.current) {
                showToast("reCAPTCHA component error. Please refresh the page.", 'error');
                setLoading(false);
                return;
            }

            const recaptchaToken = recaptchaRef.current.getValue();
            
            if (!recaptchaToken) {
                showToast("Please check the reCAPTCHA checkbox and wait for verification to complete.", 'error');
                setLoading(false);
                return;
            }

            // Trim form data
            const trimmedData = {
                name: formData.name.trim(),
                company: formData.company.trim(),
                phone: formData.phone.trim(),
                email: formData.email.trim(),
                message: formData.message.trim()
            };

            const payload = {
                ...trimmedData,
                recaptchaToken
            };
            
            const response = await axios.post(
                process.env.REACT_APP_BACKEND_URL + '/api/v1/contactus', 
                payload
            );
            
            showToast("Email sent successfully!", 'success');
            setFormData(intitalData);
            
            // Clear reCAPTCHA
            recaptchaRef.current?.reset();
            
            // Reset loading state
            setLoading(false);
            
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || "Failed to send message. Please try again.";
            showToast(errorMsg, 'error');
            
            // Reset reCAPTCHA on error
            recaptchaRef.current?.reset();
            setLoading(false);
        }
    };

    return (
        <div className="contact-container">
            {/* Left Side */}
            <div className="contact-left">
                <div style={{ display: 'flex', flexDirection: 'row', alignItems:'center', gap:16 }}>
                    <h3>Reach Out to Us</h3>
                    <img src={Arrow} style={{ marginTop:16}}/>
                </div>
                <img style={{ height: 48}} src={Logo} alt='' />
                <p>
                315 Work Avenue, 2nd Floor, Silver Utopia, Cardinal Gracious Road, Chakala, Andheri East, Mumbai- 400 099
                </p>
            </div>

            {/* Right Side */}
            <form onSubmit={handleSubmit} className="contact-right">
                <input
                type="text"
                placeholder="Name"
                id="name"
                className="contact-input"
                required 
                value={formData.name}
                onChange={handleChange}
                />
                <input
                id="company"
                type="text"
                placeholder="Company"
                required 
                value={formData.company}
                className="contact-input"
                onChange={handleChange}

                />
                <input
                id="phone"
                type="text"
                placeholder="Phone no."
                className="contact-input"
                required 
                value={formData.phone}
                onChange={handleChange}

                />
                <input
                id="email"
                type="email"
                placeholder="Email ID"
                required 
                value={formData.email}
                className="contact-input"
                onChange={handleChange}

                />
                <textarea
                id="message"
                placeholder="Message"
                className="contact-textarea"
                value={formData.message}
                onChange={handleChange}

                />
                <div>
                    <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                        onChange={onChange}
                    />
                </div>
                <button className="contact-button" disabled={loading}>
                    <span>{loading ? "Sending..." : "Send"}</span>
                    <img src={RightArrow} alt='' />
                </button>
            </form>

            {/* Toast Notification */}
            <Snackbar 
                open={toastOpen} 
                autoHideDuration={4000} 
                onClose={handleToastClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleToastClose} 
                    severity={toastType} 
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default ContactForm;
